import { DataTable, Mono, Text } from "@athena/ui";
import { formatDateTime } from "../../../../global/formatHelpers";
import type { PushNotificationHistoryFieldsFragment } from "@/graphql/pushNotifications.generated";
import { useListView } from "@/hooks/useListView";
import { ListViewControls } from "@/components/tables/ListViewControls";

interface PushNotificationHistoryProps {
    notifications: PushNotificationHistoryFieldsFragment[];
    loading: boolean;
}

/** Sent push notifications, newest first: the admin portal's history and the alumni console's. */
export function PushNotificationHistory({ notifications, loading }: PushNotificationHistoryProps) {
    /* Scroll or pages, the reader's choice, portal-wide — see `useListView`. This list only grows. */
    const historyView = useListView(notifications);

    return (
        <>
            <ListViewControls view={historyView} noun="notifications" />
            <div className="border-border mt-2 rounded-2xl border">
                <DataTable
                    columns={[
                        {
                            key: "title",
                            header: "Internal Title / Title",
                            headerClassName: "w-72",
                            className: "align-top",
                            cell: (row) => (
                                <>
                                    {row.privateSubject && (
                                        <Mono className="text-muted-foreground mb-0.5 block text-[11px] tracking-widest uppercase">
                                            {row.privateSubject}
                                        </Mono>
                                    )}
                                    <Text weight="semibold" className="text-sm">
                                        {row.subject}
                                    </Text>
                                    <Text muted className="text-xs">
                                        {formatDateTime(row.createdAt)}
                                    </Text>
                                </>
                            ),
                        },
                        {
                            key: "message",
                            header: "Message",
                            className: "align-top",
                            cell: (row) => (
                                <Text muted className="text-sm">
                                    {row.body}
                                </Text>
                            ),
                        },
                        {
                            key: "link",
                            header: "Link",
                            headerClassName: "w-52",
                            className: "align-top",
                            cell: (row) => (
                                <Text muted className="text-sm">
                                    {row.linkType === "COURSE"
                                        ? `Course: ${row.course?.title ?? "—"}`
                                        : row.linkType === "COURSE_STEP"
                                          ? `Course step: ${row.content?.title ?? "—"}`
                                          : row.linkType === "CHAT"
                                            ? "Chat"
                                            : "—"}
                                </Text>
                            ),
                        },
                        {
                            key: "sender",
                            header: "Sent By",
                            headerClassName: "w-44",
                            className: "align-top",
                            cell: (row) => (
                                <Text muted className="text-sm">
                                    {`${row.sender.firstName ?? ""} ${row.sender.lastName ?? ""}`.trim() ||
                                        row.sender.email}
                                </Text>
                            ),
                        },
                        {
                            key: "recipients",
                            header: "Recipients",
                            headerClassName: "w-24 text-right",
                            className: "text-right align-top",
                            cell: (row) => (
                                <Text weight="semibold" className="text-sm">
                                    {row.recipientCount.toLocaleString()}
                                </Text>
                            ),
                        },
                    ]}
                    rows={historyView.rows}
                    rowKey={(row) => row.id}
                    loading={loading}
                    empty={
                        <Text muted className="py-6 text-center text-sm">
                            No notifications sent yet.
                        </Text>
                    }
                />
            </div>
        </>
    );
}
